import { useEffect, useRef, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Field } from '../ui/Field';
import { Button } from '../ui/Button';
import { useCampaigns } from '../../lib/api/campaigns';
import { useCreateTask, usePatchTask, useTeam } from '../../lib/api/tasks';
import { slugify } from '../../lib/api/brands';
import { TASK_STATUSES } from '../../lib/enums';
import { MAX_ATTACHMENT_SIZE_BYTES, Task, TaskStatus } from '../../lib/types';
import { useToast } from '../ui/Toast';
import CommentsSection from '../comments/CommentsSection';
import AssigneePicker from '../tasks/AssigneePicker';
import { AssigneeStack } from '../../lib/brandAvatars';
import AttachmentsRow from '../attachments/AttachmentsRow';
import { useUploadAttachment } from '../../lib/api/attachments';
import { Icon } from '../ui/Icon';
import { plainTaskName } from '../tasks/TaskName';

const todayISO = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

interface Props {
  open: boolean;
  onClose: () => void;
  campaignId?: string;
  /** When set, the new task is linked to this asset. The asset's title is
   *  shown as a read-only chip in the modal so Doreen sees the link. */
  assetId?: number;
  assetTitle?: string;
  onCreated?: (id: string) => void;
  /** When set, modal switches to edit mode: PATCHes this task instead of creating. */
  task?: Task | null;
}

export const CreateTaskModal = ({
  open,
  onClose,
  campaignId,
  assetId,
  assetTitle,
  onCreated,
  task,
}: Props) => {
  const toast = useToast();
  const { data: campaigns = [] } = useCampaigns();
  const { data: team = [] } = useTeam();
  const createTask = useCreateTask();
  const patchTask = usePatchTask();
  const editing = !!task;

  const [name, setName] = useState('');
  const [status, setStatus] = useState<TaskStatus>('Not started');
  const [campaign, setCampaign] = useState(campaignId ?? '');
  const [due, setDue] = useState(todayISO());
  const [assigneeIds, setAssigneeIds] = useState<number[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Files queued for upload at create time. Tasks need to exist before
  // attachments can be linked, so we hold them in local state and POST
  // them after the create call succeeds.
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const pendingInputRef = useRef<HTMLInputElement>(null);
  const uploadAttachment = useUploadAttachment();

  useEffect(() => {
    if (!open) return;
    if (task) {
      setName(task.name);
      setStatus(task.status);
      setCampaign(task.campaign ?? '');
      setDue(task.due_date);
      setAssigneeIds(task.assignees ?? []);
    } else {
      setName('');
      setStatus('Not started');
      setCampaign(campaignId ?? (campaigns[0]?.id ?? ''));
      setDue(todayISO());
      setAssigneeIds([]);
      setPendingFiles([]);
    }
  }, [open, campaignId, campaigns, task]);

  const addPendingFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const accepted: File[] = [];
    for (const f of Array.from(files)) {
      if (f.size > MAX_ATTACHMENT_SIZE_BYTES) {
        toast.push(`"${f.name}" is too large (max 25 MB)`);
        continue;
      }
      accepted.push(f);
    }
    if (accepted.length > 0) {
      setPendingFiles((prev) => [...prev, ...accepted]);
    }
    if (pendingInputRef.current) pendingInputRef.current.value = '';
  };

  const removePendingFile = (idx: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const id = editing
    ? task!.id
    : (slugify(name) ? `t-${slugify(name)}-${Math.random().toString(36).slice(2, 6)}` : '');
  const pending = createTask.isPending || patchTask.isPending;
  // Task needs at least a campaign OR an asset to anchor on.
  const hasAnchor = !!campaign || !!assetId;
  const canSubmit = !!name && hasAnchor && !!id && !pending;

  const submit = async () => {
    if (!canSubmit) return;
    try {
      if (editing) {
        const updated = await patchTask.mutateAsync({
          id: task!.id,
          patch: {
            name: name.trim(),
            due_date: due,
            status,
            campaign: campaign || null,
            assignees: assigneeIds,
          } as Partial<Task>,
        });
        toast.push(`Task "${plainTaskName(updated.name)}" updated`);
        onCreated?.(updated.id);
      } else {
        const created = await createTask.mutateAsync({
          id,
          name: name.trim(),
          due_date: due,
          status: 'Not started',
          campaign: campaign || null,
          asset: assetId ?? null,
          assignees: assigneeIds,
        });
        // Now that the task exists, upload any files Doreen queued up
        // before submit. Failures are flagged but don't roll back the
        // task itself — she can retry from the edit modal.
        for (const file of pendingFiles) {
          try {
            await uploadAttachment.mutateAsync({ file, task: created.id });
          } catch {
            toast.push(`Could not upload ${file.name}`);
          }
        }
        toast.push(`Task "${plainTaskName(created.name)}" created`);
        onCreated?.(created.id);
      }
      onClose();
    } catch (err) {
      console.error(err);
      toast.push(editing ? 'Could not update task' : 'Could not create task');
    }
  };

  const selectedAssignees = team.filter((u) => assigneeIds.includes(u.user_id));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? `Edit task — ${plainTaskName(task!.name)}` : 'New task'}
      maxWidth={520}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={!canSubmit}>
            {editing ? 'Save changes' : 'Create task'}
          </Button>
        </>
      }
    >
      <Field label="Task name" required>
        <input
          className="input"
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Write IG caption"
        />
      </Field>

      {assetId && assetTitle && (
        <Field label="Linked to placement">
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 10px',
              background: 'var(--bg-subtle)',
              border: '1px solid var(--border)',
              borderRadius: 999,
              fontSize: 12,
              color: 'var(--ink-2)',
            }}
          >
            ↗ {assetTitle}
          </div>
        </Field>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label={assetId ? 'Campaign' : 'Campaign'} required={!assetId}>
          <select
            className="input"
            value={campaign}
            onChange={(e) => setCampaign(e.target.value)}
          >
            <option value="">{assetId ? '(none)' : 'Select a campaign…'}</option>
            {campaigns.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </Field>
        <Field label="Due date" required>
          <input
            className="input"
            type="date"
            value={due}
            onChange={(e) => setDue(e.target.value)}
          />
        </Field>
      </div>

      {editing && (
        <Field label="Status">
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {TASK_STATUSES.map((s) => (
              <button
                key={s}
                type="button"
                className={`filter-pill ${status === s ? 'active' : ''}`}
                onClick={() => setStatus(s as TaskStatus)}
              >
                {s}
              </button>
            ))}
          </div>
        </Field>
      )}

      <Field label="Assignees">
        <div style={{ position: 'relative' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: 6,
              border: '1px dashed var(--border-strong)',
              borderRadius: 6,
              minHeight: 40,
              cursor: 'pointer',
            }}
            onClick={() => setPickerOpen((v) => !v)}
          >
            {selectedAssignees.length === 0 ? (
              <span style={{ color: 'var(--ink-4)', fontSize: 12 }}>Click to assign teammates</span>
            ) : (
              <>
                <AssigneeStack users={selectedAssignees} size={24} />
                <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                  {selectedAssignees.length} assigned
                </span>
              </>
            )}
          </div>
          {pickerOpen && (
            <AssigneePicker
              value={assigneeIds}
              users={team}
              onChange={setAssigneeIds}
              onClose={() => setPickerOpen(false)}
            />
          )}
        </div>
      </Field>

      {editing && task && (
        <Field label="Attachments">
          <AttachmentsRow taskId={task.id} />
        </Field>
      )}

      {!editing && (
        <Field label="Attachments" hint="Files are uploaded once the task is created.">
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {pendingFiles.map((f, idx) => (
              <span
                key={`${f.name}-${idx}`}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  background: 'transparent',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--r-md)',
                  padding: '3px 4px 3px 8px',
                  fontSize: 11.5, maxWidth: 220,
                }}
              >
                <Icon name="archive" size={11} />
                <span
                  title={f.name}
                  style={{
                    color: 'var(--ink-2)', fontSize: 11.5,
                    maxWidth: 160, overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}
                >
                  {f.name}
                </span>
                <button
                  type="button"
                  onClick={() => removePendingFile(idx)}
                  className="icon-btn"
                  title="Remove"
                  style={{ width: 18, height: 18, color: 'var(--ink-4)' }}
                >
                  <Icon name="x" size={10} />
                </button>
              </span>
            ))}
            <button
              type="button"
              onClick={() => pendingInputRef.current?.click()}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                background: 'transparent',
                border: '1px dashed var(--border-strong)',
                borderRadius: 'var(--r-md)',
                padding: '3px 8px',
                fontSize: 11.5, color: 'var(--ink-3)',
                cursor: 'pointer',
              }}
            >
              <Icon name="plus" size={11} />
              {pendingFiles.length === 0 ? 'Add attachment' : 'Add'}
            </button>
            <input
              ref={pendingInputRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => addPendingFiles(e.target.files)}
            />
          </div>
        </Field>
      )}

      {editing && task && <CommentsSection taskId={task.id} />}
    </Modal>
  );
};

export default CreateTaskModal;
